import { EditOutlined, LockOutlined, UserOutlined } from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Button, Space, Switch, Table, Tooltip } from "antd"
import type React from "react"

import { useTableScroll } from "@/hooks/useTableScroll"

import "@/styles/table.full.css"

interface RoleTableProps {
  data: any[]
  loading: boolean
  onEdit: (record: any) => void
  onAssignPermissions: (record: any) => void
  onViewAccounts: (record: any) => void
  onStatusChange: (record: any, checked: boolean) => void
  pagination?: TableProps["pagination"]
}

/**
 * Component bảng danh sách vai trò (RoleTable)
 */
const RoleTable: React.FC<RoleTableProps> = ({
  data,
  loading,
  onEdit,
  onAssignPermissions,
  onViewAccounts,
  onStatusChange,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 40,
      align: "center",
      render: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Tên vai trò",
      dataIndex: "name",
      key: "name",
      width: 180,
      fixed: "left",
      ellipsis: { showTitle: true },
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      width: 180,
      ellipsis: { showTitle: true },
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      ellipsis: { showTitle: true },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 95,
      align: "center",
      render: (value: number, record) => (
        <Switch
          checked={value === 1}
          onChange={(checked) => onStatusChange(record, checked)}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      ),
    },
    {
      title: "Người sửa cuối",
      dataIndex: "editor",
      key: "editor",
      width: 140,
      render: (value: any) => value?.username || "_",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 140,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small" wrap={false}>
          <Tooltip title="Sửa vai trò">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-base! text-blue-500! hover:text-blue-600!" />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>

          <Tooltip title="Phân quyền">
            <Button
              type="text"
              size="small"
              icon={<LockOutlined className="text-amber-500! text-base! hover:text-amber-600!" />}
              onClick={() => onAssignPermissions(record)}
            />
          </Tooltip>

          <Tooltip title="Xem tài khoản thuộc vai trò này">
            <Button
              type="text"
              size="small"
              icon={<UserOutlined className="text-base! text-green-500! hover:text-green-600!" />}
              onClick={() => onViewAccounts(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

RoleTable.displayName = "RoleTable"

export default RoleTable
